import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
} from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton } from 'components';

import { ApiData } from 'scenes/common/helpers/apiData/types';
import InputQuestion from 'scenes/common/components/Questions/InputQuestion';
import {
  isIneligiblePromoCodeError,
  isPromoCodeError,
} from 'scenes/common/helpers/apiData';
import {
  customerCareNumber,
  format1300Number,
} from 'scenes/common/helpers/contacts';

const STATE_KEY = 'promoCode';
export const QUESTION = 'Enter promo code (optional)';
export const SUBTEXT = 'Only 1 promo code can be used per policy purchased.';
const INVALID_CODE_MSG =
  'Sorry, that promo code is not valid. Please try again.';
const INELIGIBLE_CODE_MSG = `The details you have entered do not meet the requirements for that promo code. Please continue to purchase this policy without the promo code or call us on ${format1300Number(
  customerCareNumber()
)} if you have any questions.`;

const MIN_LENGTH = 2;
const MAX_LENGTH = 10;
const ALPHANUMERIC_PATTERN = /^[A-Za-z0-9]*$/;

const PromoCodeStep: FunctionComponent<{
  hasQuoteNumber: boolean;
  onCreateQuote: () => void;
  onUpdateQuoteDetails: () => void;
  onUpdatePromoCode: (promoCode: string) => void;
  defaultPromoCodeStateValue: string;
  quoteDetails: ApiData<unknown>;
  onSubmitQuoteAnalytics: () => void;
}> = ({
  quoteDetails,
  onCreateQuote,
  onUpdateQuoteDetails,
  hasQuoteNumber,
  onUpdatePromoCode,
  defaultPromoCodeStateValue,
  onSubmitQuoteAnalytics,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    setError,
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = useCallback(async () => {
    onSubmitQuoteAnalytics();
    if (hasQuoteNumber) {
      onUpdateQuoteDetails();
    } else {
      onCreateQuote();
    }
  }, [
    hasQuoteNumber,
    onCreateQuote,
    onSubmitQuoteAnalytics,
    onUpdateQuoteDetails,
  ]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (!value || value.match(ALPHANUMERIC_PATTERN)) {
        onChange(value);
        onUpdatePromoCode(value);
      }
    },
    [onUpdatePromoCode]
  );

  useEffect(() => {
    if (isPromoCodeError(quoteDetails)) {
      setError(STATE_KEY, {
        type: 'manual',
        message: INVALID_CODE_MSG,
      });
    } else if (isIneligiblePromoCodeError(quoteDetails)) {
      setError(STATE_KEY, {
        type: 'manual',
        message: INELIGIBLE_CODE_MSG,
      });
    }
  }, [quoteDetails, setError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputQuestion
        errors={errors}
        control={control}
        stateKey={STATE_KEY}
        defaultValue={defaultPromoCodeStateValue}
        maxLength={MAX_LENGTH}
        changeHandler={handleChange}
        validationRules={{
          required: false,
          minLength: MIN_LENGTH,
        }}
        question={QUESTION}
        helpText={SUBTEXT}
      />
      <SubmitButton enabled={hooksForm.isValid}>Get your quote</SubmitButton>
    </form>
  );
};

export default PromoCodeStep;
